<template>
	<fieldset>
		<legend>
			Data Table
			<a class="fa fa-question-circle popover-help small"
				v-if="!isBlendedFerment"
				style="margin-left: 6px"
				role="button"
				tabindex="0"
				data-trigger="focus"
				data-container="body"
				data-placement="left"
				data-html="true"
				title="Help"
				data-content="
				<small>
					Temperature and Baume data can be entered directly into the data table below.
					To record actions performed on the ferment, lab analysis results and other comments,
					click on the Action/Lab Analysis/Comments section in the desired row to bring up a box where all of this information can be entered against the selected time point.
					Actions will be displayed in the table by the icons shown below.<br />
					<br />
					<div class='row'>
						<div class='col-xs-6'>
							<i class='fa fa-fw fa-sort-amount-asc'></i> - Pumpover<br />
							<i class='fa fa-fw fa-spinner'></i> - Aeration<br />
							<i class='fa fa-fw fa-refresh'></i> - Agitator<br />
							<i class='fa fa-fw fa-plus-square'></i> - DAP<br />
						</div>
						<div class='col-xs-6'>
							<i class='fontello-beer-1'></i> - Juice<br />
							<i class='fa fa-fw fa-tint'></i> - Water<br />
							<i class='fa fa-fw fa-flask'></i> - Acid<br />
							<i class='fa fa-fw fa-chain-broken'></i> - Overseed
						</div>
					</div>
				</small>
				">
			</a>
			<a class="fa fa-question-circle popover-help small"
				v-else
				style="margin-left: 6px"
				role="button"
				tabindex="0"
				data-trigger="focus"
				data-container="body"
				data-placement="left"
				data-html="true"
				title="Help"
				data-content="
				<small>
					The data table shows the Temperature and Baume readings for the original blend component in this fermenter (before blending) for reference only (greyed out). New values for the blend can be entered in the open section of the data table. To see the data for the other blend components, select them from the 'Blend components' dropdown box above.
				</small>
				">
			</a>
		</legend>

		<table class="table table-profile table-daily-data" cellspacing="0">
			<thead>
				<tr>
					<th class="col-day">Day</th>
					<th class="col-baume" v-if="allowBaume">Sugar ({{{ units.sugarUnit() }}})</th>
					<th class="col-temp">Temp ({{{ units.temperatureUnit() }}})</th>
					<th class="col-secondary" v-if="allowBaume">{{{ units.secondarySugarUnit() }}}</th>
					<th class="col-action">Action/Analysis</th>
					<th class="col-comments">Comments</th>
				</tr>

				<tr v-for="data in dailyData.data" :class="{'highlight-current': isCurrentDay(data.day), 'highlight-after': isFutureDay(data.day), 'highlight-locked': data.locked}">
					<td>
						<span v-if="data.day.toString().indexOf('.') > -1 && data.day.toString().split('.')[1] == 0">
							{{dayToDate(data.day.toString().slice(0, data.day.toString().indexOf('.') == -1 ? 1 : data.day.toString().indexOf('.')))}}
						</span>
						<span v-else>
							{{dayToDate(data.day)}} <span v-if="measuringFreqInteger > 2">- {{$index === 0 ? 1 : $index % measuringFreqInteger + 1}}</span>
						</span>
					</td>
					<td @click.stop="editCell($event, $index, 'baume', data)" class="baume" :class="{'info': editing.status && editing.index == $index && editing.column == 'baume'}" v-if="allowBaume">
						<div v-if="editing.status && editing.index == $index && editing.column == 'baume'">
							<input type="text" @keydown.tab="tabToNextCell($event, $index, 'temp', 'comments', data)" v-model="data.baume" class="form-control"></input>
						</div>
						<div style="width:100%" v-else>
							{{data.baume || data.baume === 0 ? parseFloat(data.baume).toFixed(2) : data.baume}} {{{data.baume || data.baume === 0 ? units.sugarUnit() : '' }}}
						</div>
					</td>
					<td @click.stop="editCell($event, $index, 'temp', data)" class="temp" :class="{'info': editing.status && editing.index == $index && editing.column == 'temp'}">
						<div v-if="editing.status && editing.index == $index && editing.column == 'temp'">
							<input type="text" class="form-control" @keydown.tab="tabToNextCell($event, $index, allowBaume ? 'secondary' : 'comments', allowBaume ? 'baume' : 'comments', data)" v-model="data.temp"></input>
						</div>
						<div v-else>
							{{data.temp ? parseFloat(data.temp).toFixed(2) : data.temp}} {{{data.temp ? units.temperatureUnit() : '' }}}
						</div>
					</td>
					<td @click.stop="editCell($event, $index, 'secondary', data)" class="secondary" :class="{'info': editing.status && editing.index == $index && editing.column == 'secondary'}">
						<div v-if="editing.status && editing.index == $index && editing.column == 'secondary'">
							<input type="text" @keydown.tab="tabToNextCell($event, $index, 'comments', 'temp', data)" v-model="dailyData.data[$index].analysis.data[simulatorSetup.secondary_sugar_units]" class="form-control"></input>
						</div>
						<div style="width:100%" v-else>
							<span v-if="simulatorSetup.secondary_sugar_units == 'rs'">
								{{dailyData.data[$index].analysis.data.rs || dailyData.data[$index].analysis.data.rs === 0 ? parseFloat(dailyData.data[$index].analysis.data.rs).toFixed(2) : dailyData.data[$index].analysis.data.rs}} {{{dailyData.data[$index].analysis.data.rs || dailyData.data[$index].analysis.data.rs === 0 ? 'g/L' : '' }}}
							</span>
							<span v-else>
								{{dailyData.data[$index].analysis.data.sg || dailyData.data[$index].analysis.data.sg === 0 ? parseFloat(dailyData.data[$index].analysis.data.sg).toFixed(4) : dailyData.data[$index].analysis.data.sg}}
							</span>
						</div>
					</td>
					<td class="action-modal" @click.stop="openActionModal($index, data)">
						<i v-if="data.action.data && data.action.data.pump_over" class="fa fa-fw fa-sort-amount-asc"></i>
						<i v-if="data.action.data && data.action.data.aeration" class="fa fa-fw fa-spinner"></i>
						<i v-if="data.action.data && data.action.data.agitator > 0" class="fa fa-fw fa-refresh"></i>
						<i v-if="data.action.data && data.action.data.dap" class="fa fa-fw fa-plus-square"></i>
						<i v-if="data.action.data && data.action.data.juice" class="fontello-beer-1"></i>
						<i v-if="data.action.data && data.action.data.water" class="fa fa-fw fa-tint"></i>
						<i v-if="data.action.data && data.action.data.acid" class="fa fa-fw fa-flask"></i>
						<i v-if="data.action.data && data.action.data.reinoculate" class="fa fa-fw fa-chain-broken"></i>
						<i v-if="!isEmptyAnalysis(data)" class="fa fa-table"></i>
					</td>
					<td @click.stop="editCell($event, $index, 'comments', data)" class="comments"  :class="{'info': editing.status && editing.index == $index && editing.column == 'comments'}">
						<div v-if="editing.status && editing.index == $index && editing.column == 'comments'">
							<input type="text" class="form-control" @keydown.tab="tabToNextCell($event, $index, allowBaume ? 'baume' : 'temp', 'temp', data)" v-model="data.comments"></input>
						</div>
						<div v-else>
							{{data.comments}}
						</div>
					</td>
				</tr>
			</thead>
		</table>

		<a v-if="canRemoveBlankRow()" @click.prevent="removeBlankRow" class="btn btn-xs btn-danger pull-right" style="margin-top: 10px; display: block;">
			<i class="fa fa-times"></i> Remove Blank Rows
		</a>

		<a v-if="missingDay" @click.prevent="addMissingRow" class="btn btn-xs btn-info pull-left" style="margin-top: 10px; display: block;">
			<i class="fa fa-plus"></i> Add Missing Day
		</a>

		<div class="modal fade" id="actions_analysis" tabindex="-1" role="dialog" aria-labelledby="actionsTitle" aria-hidden="true" v-if="editing.status && editing.index != null">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<button type="button" class="close" data-dismiss="modal"><span aria-hidden="true">&times;</span><span class="sr-only">Close</span></button>
						<h4 class="modal-title" id="actionsTitle">Actions and Analysis</h4>
					</div>
					<div class="modal-body">
						<h2 class="no-margin-top">Actions</h2>
						<div class="modal-actions">
							<div class="row">
								<div class="col-xs-6">
									<input-toggle
										name="type"
										:vmodel.sync="dailyData.data[editing.index].action.data.pump_over"
										on="Performed"
										off="None"
										on-style="primary"
										label="Pump Over"
										icon="sort-amount-asc">
									</input-toggle>
								</div>
								<div class="col-xs-6">
									<input-toggle
										name="type"
										:vmodel.sync="dailyData.data[editing.index].action.data.aeration"
										on="Performed"
										off="None"
										on-style="primary"
										label="Aeration"
										icon="spinner">
									</input-toggle>
								</div>
							</div>
							<div class="row">
								<div class="col-sm-12">
									<div class="form-group form-control-toggle-top">
										<label for="agitator" class="col-xs-12 control-label text-left"><i class="fa fa-refresh"></i> Agitator Strength</label>
										<div class="col-xs-12">
											<div class="btn-group" id="agitator-group" data-toggle="buttons" v-radio="dailyData.data[editing.index].action.data.agitator" default-value="0">
												<label class="btn btn-default active">
													<input type="radio" value="0"> Off
												</label>
												<label class="btn btn-default">
													<input type="radio" value="1"> 1
												</label>
												<label class="btn btn-default">
													<input type="radio" value="2"> 2
												</label>
												<label class="btn btn-default">
													<input type="radio" value="3"> 3
												</label>

												<agitator-definition></agitator-definition>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="row">
								<div class="col-sm-6">
									<div class="form-group">
										<label for="add_dap" class="col-xs-12 control-label text-left"><i class="fa fa-plus-square"></i> Add DAP</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="add_dap" class="form-control input-sm" v-model="dailyData.data[editing.index].action.data.dap"/>
												<span class="input-group-addon input-group-addon-narrow">mg/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="add_juice" class="col-xs-12 control-label text-left"><i class="fontello-beer-1"></i> Add juice</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="add_juice" class="form-control input-sm" v-model="dailyData.data[editing.index].action.data.juice"/>
												<span class="input-group-addon input-group-addon-narrow">L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="add_water" class="col-xs-12 control-label text-left"><i class="fa fa-tint"></i> Add Water</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="add_water" class="form-control input-sm" v-model="dailyData.data[editing.index].action.data.water" />
												<span class="input-group-addon input-group-addon-narrow">L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="add_acid" class="col-xs-12 control-label text-left"><i class="fa fa-flask"></i> Add Acid</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="add_acid" class="form-control input-sm" v-model="dailyData.data[editing.index].action.data.acid"/>
												<span class="input-group-addon input-group-addon-narrow">L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="add_acid" class="col-xs-12 control-label text-left">
											<i class="fa fa-chain-broken"></i> Overseed

											<a class="fa fa-question-circle popover-help" style="margin-left: 7px"
												tabindex="0"
												role="button"
												data-placement="left"
												data-html="true"
												data-trigger="focus"
												data-container="body"
												title="Help"
												data-content="
												<small style='margin-bottom: 10px; display: block'>
													If propagating, you can use the following equation to convert your cell concentration:
												</small>
												<small style='margin-bottom: 10px; display: block'>
													<code>Cell conc (cells/ml) x pitch rate (decimal) x 5x10^-5</code>
												</small>
												<small style='margin-bottom: 10px; display: block'>
													For example:
												</small>
												<small>
													<code>Equivalent dry yeast = 1x10^8 x 0.05 x 5x10^-5 = 250mg/L</code>
												</small>">
											</a>
										</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="add_acid" class="form-control input-sm" v-model="dailyData.data[editing.index].action.data.reinoculate"/>
												<span class="input-group-addon input-group-addon-narrow">mg/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6" v-if="ferment.id !== null && ferment.id !== undefined">
									<div class="form-group">
										<label class="col-xs-12">&nbsp;</label>
										<div class="col-xs-12">
											<button
												type="button"
												:disabled="editingIndexBeforeExistingMoveEvent"
												@click.prevent="openMoveModal"
												class="m-t-md btn btn-sm btn-primary">
												Move Ferment...
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>

						<h2>Lab Analysis</h2>
						<div class="modal-actions">
							<div class="row">
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_cell_count" class="col-xs-12 control-label text-left">Cell Count</label>
										<div class="col-xs-12">
											<input type="text" name="ana_cell_count" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.cell_count">
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_ph" class="col-xs-12 control-label text-left">pH</label>
										<div class="col-xs-12">
											<input type="text" name="ana_ph" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.ph">
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_ta" class="col-xs-12 control-label text-left">TA</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_ta" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.ta">
												<span class="input-group-addon input-group-addon-narrow">g/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_fs02" class="col-xs-12 control-label text-left">FS0<sub>2</sub></label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_fs02" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.fs02">
												<span class="input-group-addon input-group-addon-narrow">mg/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_ts02" class="col-xs-12 control-label text-left">TS0<sub>2</sub></label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_ts02" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.ts02">
												<span class="input-group-addon input-group-addon-narrow">mg/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_gf" class="col-xs-12 control-label text-left">G/F</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_gf" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.gf">
												<span class="input-group-addon input-group-addon-narrow">g/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_ntu" class="col-xs-12 control-label text-left">NTU</label>
										<div class="col-xs-12">
											<input type="text" name="ana_ntu" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.ntu">
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_alcohol" class="col-xs-12 control-label text-left">Alcohol</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_alcohol" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.alcohol">
												<span class="input-group-addon input-group-addon-narrow">%</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label for="ana_malic_acid" class="col-xs-12 control-label text-left">Malic Acid</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_malic_acid" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.malic_acid">
												<span class="input-group-addon input-group-addon-narrow">g/L</span>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group" v-if="simulatorSetup.secondary_sugar_units == 'rs'">
										<label for="ana_malic_acid" class="col-xs-12 control-label text-left">SG</label>
										<div class="col-xs-12">
											<input type="text" name="ana_sg" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.sg">
										</div>
									</div>
									<div class="form-group" v-else>
										<label for="ana_malic_acid" class="col-xs-12 control-label text-left">RS</label>
										<div class="col-xs-12">
											<div class="input-group">
												<input type="text" name="ana_sg" class="form-control input-sm" v-model="dailyData.data[editing.index].analysis.data.rs">
												<span class="input-group-addon input-group-addon-narrow">g/L</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>
		<move-ferment
			v-if="isMovingFerment"
			:index="editing.index"
			:day="getDayFromIndex(editing.index)"
			:measuring-freq-integer="measuringFreqInteger">
		</move-ferment>
	</fieldset>
</template>

<style>
	tr.highlight-current td {
		background-color: #f9f5e3;
	}

	tr.highlight-after td {
		background-color: #e0ecff;
	}

	tr.highlight-locked td {
		cursor: not-allowed;
		background-color: #efefef;
		color: #999;
	}

	tr.highlight-locked .action-modal {
		cursor: not-allowed;
	}

	.table-daily-data td {
		transition: background-color 0.1s linear;
	}

	.table-daily-data input {
		border: none !important;
		padding: 0 !important;
		line-height: 1 !important;
		height: auto !important;
		background: none !important;
	}
</style>

<script>
	import { simulatorSetup } from 'vx/getters'
	import Vue from 'vue'
	import moment from 'moment'
	import Notify from 'utils/notify'
	import units from 'utils/units'
	import tabbing from './daily-data/tabbing'
	import rowHelpers from './daily-data/row-helpers'
	import dayHelpers from './daily-data/day-helpers'
	import AgitatorDefinition from 'components/ferment/agitator-definition'
	import { caluculateMeasuringFreq } from 'utils/ferment'
	import { filter, merge, findLastIndex, slice, concat, clone } from 'lodash'
	import MoveFerment from './move-ferment'

	export default {
		components: {
			AgitatorDefinition,
			MoveFerment
		},

		vuex: {
			getters: {
				simulatorSetup
			}
		},

		computed: {
			editingIndexBeforeExistingMoveEvent () {
				if (!this.editing.status || !this.ferment || !this.ferment.previousFermenters) {
					return false
				}

				let moveRegex = new RegExp('Transferred from', 'ig')

				for (let i = this.editing.index; i < this.dailyData.data.length; i++) {
					if (this.dailyData.data[i].comments && moveRegex.test(this.dailyData.data[i].comments)) {
						return true;
					}
				}

				return false;
			},

			isBlendedFerment () {
				return this.ferment &&
					this.ferment.blendComponents &&
					this.ferment.blendComponents.data.length > 0
			}
		},

		props: {
			editingDisabled: {
				type: Boolean,
				required: false,
				default: false
			},
			dailyData: {
				required: true
			},
			allowBaume: {
				required: false,
				default: true
			},
			fermentType: {},
			measuringFreq: {
				required: false,
				default: null
			},
			measuringFreqInteger: {
				required: true
			},
			archived: {},
			daysAsDates: {
				type: Boolean,
				required: false,
				default: false
			},
			startDate: {
				type: String,
				required: false,
			},
			ferment: {
				required: false,
				default: null
			}
		},

		data() {
			return {
				units,
				editing: {
					status: false,
					index: null,
					column: null
				},
				isEditingAction: false,
				isMovingFerment: false,
				freq: 0,
				missingDay: false
			};
		},

		ready() {
			this.formatDailyData()

			$(window).on('hidden.bs.modal', () => {
				this.editing.index = null;
				this.editing.status = false;
				this.isEditingAction = false;

				$('#agitator-group').find('.active').removeClass('active');
			});

			$(window).on('show.bs.modal', () => {
				if (this.editingDisabled) {
					$('.modal-content input').prop('disabled', true);
					$('.modal-content label').addClass('disabled');

					$('.modal-content label.disabled').on('click.disabled', event => {
						event.preventDefault();

						return false;
					});
				} else {
					$('.modal-content input').prop('disabled', false);
					$('.modal-content label').removeClass('disabled');

					$('.modal-content label').off('click.disabled');
				}
			});
		},

		watch: {
			dailyData: {
				handler() {
					this.formatDailyData()
				},
				deep: true
			},

			missingDay(newVal, oldVal) {
				// fix wrong end day
				if (newVal === false && oldVal === true) {
					this.dailyData.data.pop()
				}
			}
		},

		events: {
			isBlankRow(row, callback) {
				if (this.isBlankRow(row)) {
					callback();
				}
			},

			clearDailyData() {
				this.dailyData.data = [];
			}
		},

		methods: {
			...tabbing,
			...rowHelpers,
			...dayHelpers,

			formatDailyData() {
				this.fixBlankDailyData();
				this.missingDay = false

				let lastRow = this.dailyData.data[this.dailyData.data.length - 1];

				if (! this.isBlankRow(lastRow)) {
					this.dailyData.data.push(this.newBlankDay());
				}

				// we need at leash 2 data to calculate current frequency
				if (this.dailyData.data.length >= 3) {
					const currentDataFrequency = Math.round(1 / (this.dailyData.data[1].day - this.dailyData.data[0].day))
					// frequency changed e.g. from 2 to 3, from 3 to 2
					if (currentDataFrequency != this.measuringFreqInteger) {
						// console.log(currentDataFrequency, this.measuringFreqInteger)
						for (let i = 0; i < this.dailyData.data.length - 1; i++) {
							this.dailyData.data[i].day = this.getDayFromIndex(i)
						}

						// force trigger data change
						Vue.nextTick(() => {
							this.dailyData.data = concat([], this.dailyData.data)
							this.$dispatch('forceUpdateData')
						})
					}
				}

				// Quickly spin through all the day data and remove any bad values. Basically,
				// anything that is not a number for temp and baume.
				for (let i = 0; i < this.dailyData.data.length - 1; i++) {
					const dayData = this.dailyData.data[i]

					if (isNaN(dayData.baume)) {
						dayData.baume = null;
					}

					if (isNaN(dayData.temp)) {
						dayData.temp = null;
					}

					// update in a correct order
					// in case user delete some data in the middle
					const day = this.getDayFromIndex(i)
					if (dayData.day != day) {
						this.missingDay = true
					}
				}
			},

			addMissingRow() {
				for (let i = 0; i < this.dailyData.data.length - 1; i++) {
					const day = this.getDayFromIndex(i)
					if (this.dailyData.data[i].day != day) {
						// console.log(this.dailyData.data[i].day, day)
						const missingRow = merge(this.newBlankDay(), { day })
						this.dailyData.data.splice(i, 0, missingRow)
						break
					}
				}
			},

			getDayFromIndex(index) {
				return parseFloat((index / this.measuringFreqInteger).toFixed(2))
			},

			isCurrentDay(day) {
				return moment().isSame(moment(this.startDate, 'DD/MM/YYYY hh:mm A').add(day * 24, 'hours'), 'day');
			},

			isFutureDay(day) {
				return moment().isBefore(moment(this.startDate, 'DD/MM/YYYY hh:mm A').add(day * 24, 'hours'), 'day');
			},

			dayToDate(day) {
				if (! this.daysAsDates || ! this.startDate || ! this.simulatorSetup.date_day_display) {
					return day;
				}

				let format = 'DD/MM/YYYY';

				if (this.dayIncrement() == 0.5) {
					format = 'DD/MM/YYYY A';
				}

				return moment(this.startDate, 'DD/MM/YYYY hh:mm A').add(day * 24, 'hours').format(format);
			},

			/**
			 * Remove the last blank row.
			 *
			 * @return void
			 */
			removeBlankRow() {
				this.editing = {
					status: false,
					index: null,
					column: null
				};

				const lastNonBlankRow = findLastIndex(this.dailyData.data, row => !this.isBlankRow(row))
				Vue.nextTick(() => {
					this.dailyData.data = slice(this.dailyData.data, 0, lastNonBlankRow + 1)
				})
			},

			/**
			 * Determine if blank rows can be removed.
			 *
			 * @return bool
			 */
			canRemoveBlankRow() {
				if (this.dailyData.data && this.dailyData.data.length > 1) {
					const lastNonBlankRow = findLastIndex(this.dailyData.data, row => !this.isBlankRow(row))

					return lastNonBlankRow < this.dailyData.data.length - 2
				}

				return false;
			},

			/**
			 * The day increment is defined within the simulator setup. If fermentType is true
			 * then it's a red, if false, it's a white.
			 *
			 * @return int
			 */
			dayIncrement() {
				if (this.measuringFreq) {
					return parseFloat(this.measuringFreq);
				}

				// We'll get the frequency variable name on the simulator setup based on
				// the ferment type selected.
				const frequencyVariable = this.fermentType
					? 'red_sampling_freq'
					: 'white_sampling_freq';

				return caluculateMeasuringFreq(this.simulatorSetup[frequencyVariable]);
			},

			getNextDay() {
				const dataLen = this.dailyData.data.length;

				if (dataLen === 0) {
					return 0;
				}

				const nextDay = parseFloat(parseFloat(dataLen / this.measuringFreqInteger).toFixed(2));

				return dataLen % this.measuringFreqInteger === 0
					? Math.round(nextDay)
					: nextDay;
			},

			/**
			 * When tabbing down to a blank row we push a new one on the end.
			 *
			 * @return void
			 */
			handleTabToBlankRow(event, index, previous, next) {
				this.dailyData.data.push(this.newBlankDay());

				this.editing = {
					status: true,
					index,
					column: event.shiftKey ? previous : next
				};
			},

			/**
			 * Find the next row from the current row when tabbing.
			 *
			 * @param object row
			 *
			 * @return object
			 */
			findNextRow(row) {
				return row.next('tr');
			},

			/**
			 * Fix blank daily data by adding a single blank row.
			 *
			 * @return void
			 */
			fixBlankDailyData() {
				if (this.dailyData.data.length == 0) {
					this.editing = {
						status: false,
						index: null,
						column: null
					};

					this.dailyData.data = [this.newBlankDay()];
				}
			},

			openActionModal(index, data) {
				if (data.locked && !this.archived) return

				this.editing.status = true;
				this.editing.index = index;
				this.editing.column = null
				this.isEditingAction = true;

				Vue.nextTick(() => {
					$('#actions_analysis').modal();
				});
			},

			openMoveModal() {
				this.isMovingFerment = true

				let editing = clone(this.editing)

				$('#actions_analysis').one('hidden.bs.modal', () => {
					$('#move_ferment').one('show.bs.modal', () => {
						this.$nextTick(() => {
							this.editing = clone(editing)
						})
					}).modal('show');
				}).modal('hide');
			}
		}
	}
</script>



// WEBPACK FOOTER //
// daily-data.vue?198b573e